/**
 * Invite API functions
 * Feature: 012-care-seeker-invite-signup
 * 
 * API client for invite-related operations.
 * Supports public invite retrieval for signup flow and
 * professional invite management (create, list, resend).
 */

import { apiClient } from '@/core/api/client';
import type {
  AcceptInviteRequest,
  AcceptInviteResponse,
  CreateInviteRequest,
  GetInvitesQuery,
  InviteListResponse,
  InvitePublicResponse,
  InviteResponse,
  ReferralCodeInfo,
  ResendCooldownError,
} from '../types/invite';

/**
 * API Error type for invite operations
 */
export type InviteApiError = {
  message: string;
  statusCode: number;
  error?: string;
  retryAfter?: number;
  resendAvailableAt?: string;
};

/**
 * Fetch invite details by ID for signup page pre-fill
 * Public endpoint - no authentication required
 * 
 * @param inviteId - UUID of the invite from the signup link
 * @returns Invite public details (firstName, lastName, email, inviterName, inviterCompany)
 * @throws InviteApiError with statusCode 404 if invite not found or already accepted
 * 
 * @example
 * ```ts
 * const invite = await getInviteById('550e8400-e29b-41d4-a716-446655440000');
 * console.log(invite.firstName); // "John"
 * ```
 */
export const getInviteById = async (
  inviteId: string,
): Promise<InvitePublicResponse> => {
  const response = await apiClient.get<InvitePublicResponse>(
    `/invites/${inviteId}`,
  );
  return response.data;
};

/**
 * Accept an invite and create user account (server-side Kinde registration)
 * Public endpoint - no authentication required
 * 
 * This function:
 * 1. Creates user in Kinde IDP (server-side)
 * 2. Creates user in database with CareSeeker profile
 * 3. Creates professional-client relationship
 * 4. Marks invite as accepted
 * 
 * @param inviteId - UUID of the invite from the signup link
 * @param data - User information (firstName, lastName, email)
 * @returns Response with email for login page pre-fill
 * @throws InviteApiError with statusCode 400 if email mismatch or user exists
 * @throws InviteApiError with statusCode 404 if invite not found or already accepted
 * 
 * @example
 * ```ts
 * const result = await acceptInvite('550e8400-...', {
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   email: 'john@example.com'
 * });
 * // Redirect to login with email pre-filled
 * navigate(`/login?email=${encodeURIComponent(result.email)}`);
 * ```
 */
export const acceptInvite = async (
  inviteId: string,
  data: AcceptInviteRequest,
): Promise<AcceptInviteResponse> => {
  const response = await apiClient.post<AcceptInviteResponse>(
    `/invites/${inviteId}/accept`,
    data,
  );
  return response.data;
};

/**
 * Create a new invite for a care seeker
 * Protected endpoint - requires JWT authentication
 * 
 * @param data - Invite data (firstName, lastName, email)
 * @returns Created invite details
 * @throws InviteApiError with statusCode 400 if email already registered or pending invite exists
 * @throws InviteApiError with statusCode 401 if not authenticated
 * @throws InviteApiError with statusCode 403 if user is not a professional
 * 
 * @example
 * ```ts
 * const invite = await createInvite({
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   email: 'john@example.com'
 * });
 * ```
 */
export const createInvite = async (
  data: CreateInviteRequest,
): Promise<InviteResponse> => {
  const response = await apiClient.post<InviteResponse>('/invites', data);
  return response.data;
};

/**
 * Get invites created by the current user
 * Protected endpoint - requires JWT authentication
 * 
 * @param query - Optional filters and pagination (status, page, limit)
 * @returns Paginated list of invites with metadata
 * @throws InviteApiError with statusCode 401 if not authenticated
 * 
 * @example
 * ```ts
 * // Get all invites
 * const invites = await getInvitesByUser();
 * 
 * // Get pending invites with pagination
 * const pending = await getInvitesByUser({ status: 'pending', page: 1, limit: 10 });
 * ```
 */
export const getInvitesByUser = async (
  query?: GetInvitesQuery,
): Promise<InviteListResponse> => {
  const params = new URLSearchParams();
  
  if (query?.status) {
    params.append('status', query.status);
  }
  if (query?.page) {
    params.append('page', query.page.toString());
  }
  if (query?.limit) {
    params.append('limit', query.limit.toString());
  }
  
  const queryString = params.toString();
  const url = queryString ? `/invites/by-user?${queryString}` : '/invites/by-user';
  
  const response = await apiClient.get<InviteListResponse>(url);
  return response.data;
};

/**
 * Resend invite result type
 * Returns either success response or cooldown error info
 */
export type ResendInviteResult = 
  | { success: true; invite: InviteResponse }
  | { success: false; cooldown: ResendCooldownError };

/**
 * Resend an invite email
 * Protected endpoint - requires JWT authentication
 * Subject to 30-minute cooldown from last send
 * 
 * @param inviteId - UUID of the invite to resend
 * @returns Result with either success/invite or cooldown info
 * 
 * @example
 * ```ts
 * const result = await resendInvite('550e8400-e29b-41d4-a716-446655440000');
 * if (result.success) {
 *   console.log('Email sent!');
 * } else {
 *   console.log(`Wait ${result.cooldown.retryAfter} seconds`);
 * }
 * ```
 */
export const resendInvite = async (
  inviteId: string,
): Promise<ResendInviteResult> => {
  try {
    const response = await apiClient.post<InviteResponse>(
      `/invites/${inviteId}/resend`,
    );
    return { success: true, invite: response.data };
  } catch (error) {
    const apiError = error as InviteApiError;
    if (apiError.statusCode === 429) {
      return {
        success: false,
        cooldown: {
          statusCode: 429,
          message: apiError.message,
          error: 'Too Many Requests',
          retryAfter: apiError.retryAfter ?? 0,
          resendAvailableAt: apiError.resendAvailableAt ?? '',
        },
      };
    }
    throw error;
  }
};

/**
 * Check if an error is an invite-related API error
 */
export const isInviteNotFoundError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null && 'statusCode' in error) {
    return (error as InviteApiError).statusCode === 404;
  }
  return false;
};

/**
 * Check if an error is a rate limit error (429)
 */
export const isRateLimitError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null && 'statusCode' in error) {
    return (error as InviteApiError).statusCode === 429;
  }
  return false;
};

/**
 * Check if email is already registered or has pending invite
 */
export const isEmailConflictError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null && 'statusCode' in error) {
    return (error as InviteApiError).statusCode === 400;
  }
  return false;
};

/**
 * Validate a referral code and get professional information
 * Public endpoint - no authentication required
 * 
 * Note: This endpoint validates the referral code and returns information
 * about the professional who shared it, without exposing sensitive data.
 * 
 * @param code - Alphanumeric referral code from URL
 * @returns Professional info (name, company, role) or null if invalid
 * 
 * @example
 * ```ts
 * const info = await validateReferralCode('ABCD1234');
 * if (info) {
 *   console.log(`Referred by: ${info.professionalName}`);
 * }
 * ```
 */
export const validateReferralCode = async (
  code: string,
): Promise<ReferralCodeInfo | null> => {
  try {
    const response = await apiClient.get<ReferralCodeInfo>(
      `/invites/referral-code/${code}/validate`,
    );
    return response.data;
  } catch (error) {
    const apiError = error as InviteApiError;
    // Return null for 404 (invalid code) - let the UI handle gracefully
    if (apiError.statusCode === 404) {
      return null;
    }
    // Re-throw other errors
    throw error;
  }
};
